import React, { useState } from "react";
import { MailWarning, Loader2, X, MailPlus, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Modal } from "@/components/ui/modal";
import { useAuth } from "@/context/AuthContext";

// Shown at the top of the app when a signed-in email account is not yet verified.
export function VerifyBanner() {
  const { user, resendVerification, recoverEmail } = useAuth();
  const [busy, setBusy] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [fixOpen, setFixOpen] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [sentTo, setSentTo] = useState("");

  if (!user || user.email_verified || hidden) return null;

  const resend = async () => {
    setBusy(true);
    try {
      await resendVerification();
      toast("Verification email sent", { description: `Check ${user.email} for your confirm link.` });
    } catch (e) {
      toast("Couldn't send email", { description: e.message });
    } finally { setBusy(false); }
  };

  const submitFix = async () => {
    const email = newEmail.trim().toLowerCase();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) { toast("Enter a valid email address"); return; }
    setSending(true);
    try {
      await recoverEmail(email);
      setSentTo(email);
    } catch (e) { toast("Couldn't update email", { description: e.message }); }
    finally { setSending(false); }
  };

  return (
    <>
      <div data-testid="verify-banner" className="flex flex-wrap items-center gap-x-3 gap-y-1.5 border-b border-aura-gold/20 bg-aura-gold/[0.06] px-4 py-2.5 text-sm">
        <MailWarning className="h-4 w-4 shrink-0 text-aura-gold" />
        <p className="min-w-0 flex-1 text-am-text2">
          <span className="font-semibold text-white">Confirm your email</span> to start posting.
          We sent a link to <span className="text-white">{user.email}</span>.
        </p>
        {user.allow_email_recovery && (
          <button onClick={() => setFixOpen(true)} data-testid="fix-email-open" className="flex shrink-0 items-center gap-1.5 rounded-full aura-gradient px-3 py-1.5 text-xs font-bold text-black">
            <MailPlus className="h-3.5 w-3.5" /> Fix your email
          </button>
        )}
        <button onClick={resend} disabled={busy} data-testid="verify-resend" className="flex shrink-0 items-center gap-1.5 rounded-full bg-white/10 px-3 py-1.5 text-xs font-semibold text-white hover:bg-white/20 disabled:opacity-60">
          {busy && <Loader2 className="h-3 w-3 animate-spin" />} Resend
        </button>
        <button onClick={() => setHidden(true)} data-testid="verify-dismiss" className="shrink-0 text-am-text3 hover:text-white"><X className="h-4 w-4" /></button>
      </div>

      <Modal open={fixOpen} onClose={() => !sending && setFixOpen(false)} title="Fix your email" testid="fix-email-modal">
        <div className="p-4">
          {sentTo ? (
            <div className="py-4 text-center" data-testid="fix-email-sent">
              <CheckCircle2 className="mx-auto h-10 w-10 text-aura-mint" />
              <h3 className="mt-3 font-display text-lg font-bold text-white">Check your new inbox</h3>
              <p className="mt-2 text-sm text-am-text2">We sent a confirmation link to <span className="text-white">{sentTo}</span>. Open it to finish switching your email. Your email won't change until you confirm it.</p>
              <button onClick={() => { setFixOpen(false); }} className="mt-5 w-full rounded-full border border-am-border-strong py-2.5 text-sm font-semibold text-white hover:bg-white/5">Done</button>
            </div>
          ) : (
            <>
              <p className="text-sm text-am-text2">Signed up with the wrong email? Enter a new address you control. We'll send a confirmation link there — your account, posts, and Aura stay exactly the same.</p>
              <input value={newEmail} onChange={(e) => setNewEmail(e.target.value)} type="email" data-testid="fix-email-input" placeholder="you@newemail.com" className="mt-4 w-full rounded-xl border border-am-border bg-am-surface2 px-4 py-2.5 text-sm text-white outline-none focus:border-am-border-strong" />
              <button onClick={submitFix} disabled={sending} data-testid="fix-email-submit" className="mt-3 flex w-full items-center justify-center gap-2 rounded-full aura-gradient py-3 text-sm font-bold text-black disabled:opacity-60">
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <MailPlus className="h-4 w-4" />} Send confirmation link
              </button>
            </>
          )}
        </div>
      </Modal>
    </>
  );
}
